import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Download, FileText, Edit3, Type } from 'lucide-react';
import { useErrorHandler } from '@/hooks/use-error-handler';
import OnboardingWrapper from '@/components/ui/OnboardingWrapper';
import { EXTENDED_STEPS } from '@/utils/onboarding-steps';
import { useUpdateArchitectAgreements, useUpdateSubcontractorAgreements } from '@/hooks/use-agreements';
import { agreementsApi } from '@/services/agreements';
import { useGetArchitect, useGetSubcontractor } from '@/hooks/use-contractors';
import SignaturePad, { SignaturePadRef } from '@/components/ui/SignaturePad';
import { Input } from '@/components/ui/input';

const agreementsSchema = z.object({
    hasReadProfessionalServicesAgreement: z.boolean().refine(val => val === true, 'You must read and agree to the Professional Services Agreement'),
    digitalSignature: z.string().min(2, 'Digital signature is required and must be at least 2 characters'),
    signatureType: z.enum(['text', 'drawn']).optional(),
});

type AgreementsFormData = z.infer<typeof agreementsSchema>;

interface RequiredDocument {
    id: string;
    title: string;
    description: string;
    downloadUrl: string;
}

const requiredDocuments: RequiredDocument[] = [
    {
        id: 'professional-services-agreement',
        title: 'Professional Services Agreement',
        description: 'Terms and conditions for our partnership',
        downloadUrl: '/documents/LarkPM-Professional-Services-Agreement.pdf',
    },
    {
        id: 'w9-irs-form',
        title: 'W-9 IRS Form',
        description: 'Tax information request form',
        downloadUrl: '/documents/W-9-IRS-Form.pdf',
    },
];

export default function AgreementsStep() {
    const [searchParams] = useSearchParams();
    const { handleError } = useErrorHandler();
    const signaturePadRef = useRef<SignaturePadRef>(null);
    const [signatureMode, setSignatureMode] = useState<'text' | 'drawn'>('text');

    const {
        register,
        handleSubmit,
        setValue,
        watch,
        trigger,
        formState: { errors, isSubmitting },
    } = useForm<AgreementsFormData>({
        resolver: zodResolver(agreementsSchema),
        mode: 'onChange',
        defaultValues: {
            hasReadProfessionalServicesAgreement: false,
            digitalSignature: '',
            signatureType: 'text',
        },
    });

    const organizationId = searchParams.get('orgId');
    const accountType = searchParams.get('type') as 'architect' | 'subcontractor';

    // Fetch data based on account type
    const { data: architectData, isLoading: isLoadingArchitect } = useGetArchitect(
        accountType === 'architect' ? organizationId : null
    );
    const { data: subcontractorData, isLoading: isLoadingSubcontractor } = useGetSubcontractor(
        accountType === 'subcontractor' ? organizationId : null
    );

    const isLoadingData = isLoadingArchitect || isLoadingSubcontractor;
    const currentData = accountType === 'architect' ? architectData : subcontractorData;

    // Agreement mutations
    const updateArchitectAgreements = useUpdateArchitectAgreements();
    const updateSubcontractorAgreements = useUpdateSubcontractorAgreements();

    const watchedAgreement = watch('hasReadProfessionalServicesAgreement');

    // Prefill form with existing data
    useEffect(() => {
        if (!currentData) return;

        const specificProfile = accountType === 'architect'
            ? (currentData as any).architectSpecificProfile
            : (currentData as any).subcontractorSpecificProfile;

        if (specificProfile && (specificProfile.agreementAccepted !== undefined || specificProfile.digitalSignature)) {
            setValue('hasReadProfessionalServicesAgreement', specificProfile.agreementAccepted || false);

            const signature = specificProfile.digitalSignature || '';
            setValue('digitalSignature', signature);

            // Determine signature type based on data format
            if (signature.startsWith('data:image/')) {
                setSignatureMode('drawn');
                setValue('signatureType', 'drawn');
            } else if (signature.trim().length > 0) {
                setSignatureMode('text');
                setValue('signatureType', 'text');
            }

            // Clear any validation errors if data is prefilled
            if (specificProfile.agreementAccepted) {
                trigger('hasReadProfessionalServicesAgreement');
            }
            if (specificProfile.digitalSignature) {
                trigger('digitalSignature');
            }
        }
    }, [currentData, setValue, accountType, trigger, setSignatureMode]);

    const handleNext = async (data: AgreementsFormData) => {
        if (!organizationId) {
            return false;
        }

        // Validate required fields - trigger form validation instead of showing toast
        const isSignatureValid = signatureMode === 'drawn'
            ? data.digitalSignature && data.digitalSignature.startsWith('data:image/')
            : data.digitalSignature && data.digitalSignature.trim().length >= 2;

        if (!data.hasReadProfessionalServicesAgreement || !isSignatureValid) {
            // Trigger validation for all fields to show inline errors
            await trigger();
            return false;
        }

        try {
            // Get user's IP address
            const ipAddress = await agreementsApi.getUserIpAddress();

            // Prepare the payload
            const payload = {
                agreementAccepted: data.hasReadProfessionalServicesAgreement,
                digitalSignature: data.digitalSignature.trim(),
                signatureDate: new Date().toISOString().split('T')[0], // YYYY-MM-DD format
                signatureIpAddress: ipAddress,
            };

            console.log('Saving agreements:', payload);

            // Update agreements based on account type
            if (accountType === 'architect') {
                await updateArchitectAgreements.mutateAsync({
                    architectId: organizationId,
                    data: payload,
                });
            } else {
                await updateSubcontractorAgreements.mutateAsync({
                    subcontractorId: organizationId,
                    data: payload,
                });
            }

            console.log('Agreements saved successfully');
            return true;
        } catch (error: any) {
            console.error('Failed to save agreements:', error);
            handleError(error, {
                customMessage: 'Failed to save agreements. Please try again.',
            });
            return false;
        }
    };

    return (
        <OnboardingWrapper
            currentStep={5}
            totalSteps={7}
            steps={EXTENDED_STEPS}
            accountType={accountType}
            onNext={async () => {
                const isValid = await trigger();
                if (!isValid) return false;

                return new Promise((resolve) => {
                    handleSubmit(async (data) => {
                        const result = await handleNext(data);
                        resolve(result !== false);
                    })();
                });
            }}
            isLoading={isSubmitting || updateArchitectAgreements.isPending || updateSubcontractorAgreements.isPending || isLoadingData}
            saveButtonText="Save & Continue"
        >
            <div className="space-y-6">
                <div>
                    <h2 className="text-2xl font-bold tracking-tight text-foreground mb-2">
                        Agreements
                    </h2>
                    <p className="text-muted-foreground">
                        Please review and agree to the following documents, then provide your digital signature to complete the process.
                    </p>
                </div>

                {/* Progress Indicator */}
                <div className="space-y-2">
                    {(() => {
                        const agreementCompleted = watchedAgreement;
                        const signature = watch('digitalSignature') || '';
                        const signatureCompleted = signatureMode === 'drawn'
                            ? signature.startsWith('data:image/')
                            : signature.trim().length >= 2;
                        const totalCompleted = (agreementCompleted ? 1 : 0) + (signatureCompleted ? 1 : 0);
                        const allCompleted = agreementCompleted && signatureCompleted;

                        return (
                            <>
                                <p className={`text-sm font-medium ${allCompleted ? 'text-green-700' : 'text-foreground'}`}>
                                    Progress: {totalCompleted} of 2 required steps completed
                                </p>
                                {!allCompleted && (
                                    <p className="text-sm text-red-600 font-medium">
                                        ⚠️ Both agreement acceptance and digital signature are required to continue
                                    </p>
                                )}
                                {allCompleted && (
                                    <p className="text-sm text-green-600 font-medium">
                                        ✓ All requirements completed - ready to proceed
                                    </p>
                                )}
                            </>
                        );
                    })()}
                </div>

                {/* Show loading state while fetching existing data */}
                {isLoadingData && (
                    <div className="flex items-center justify-center py-4">
                        <div className="w-6 h-6 border-2 border-outline-variant border-t-slate-600 rounded-full animate-spin" />
                        <span className="ml-2 text-muted-foreground">
                            Loading existing data...
                        </span>
                    </div>
                )}

                {/* Content - only show when not loading */}
                {!isLoadingData && (
                    <>
                        {/* Required Documents */}
                        <div>
                            <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground mb-2">
                                Required Documents
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                                Please download and review all documents before proceeding. You must accept the agreement below after reviewing.
                            </p>
                            <Card className="border border-border/15">
                                <CardContent className="p-6">
                                    <div className="space-y-4">
                                        {requiredDocuments.map((doc) => (
                                            <div key={doc.id} className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-muted transition-colors">
                                                <div className="flex items-center gap-3">
                                                    <FileText className="h-5 w-5 text-yellow-600" />
                                                    <div>
                                                        <h4 className="font-medium text-foreground">{doc.title}</h4>
                                                        <p className="text-sm text-muted-foreground">{doc.description}</p>
                                                    </div>
                                                </div>
                                                <Button
                                                    type="button"
                                                    variant="outline"
                                                    size="sm"
                                                    className="flex items-center gap-2 hover:bg-yellow-50 hover:border-yellow-300"
                                                    onClick={() => {
                                                        const link = document.createElement('a');
                                                        link.href = doc.downloadUrl;
                                                        link.download = doc.downloadUrl.split('/').pop() || doc.title;
                                                        link.target = '_blank';
                                                        link.click();
                                                    }}
                                                >
                                                    <Download className="h-4 w-4" />
                                                    Download
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </CardContent>
                            </Card>
                        </div>

                        {/* Agreement Checkbox */}
                        <div>
                            <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground mb-4">
                                Agreement Acceptance<span className="text-red-600">*</span>
                            </h3>
                            <Card className={`border ${errors.hasReadProfessionalServicesAgreement ? 'border-red-300 bg-red-50' : 'border-border'}`}>
                                <CardContent className="p-6">
                                    <div className="flex items-start space-x-3">
                                        <Checkbox
                                            id="agreement"
                                            checked={watchedAgreement}
                                            onCheckedChange={(checked) => {
                                                setValue('hasReadProfessionalServicesAgreement', checked as boolean);
                                                trigger('hasReadProfessionalServicesAgreement');
                                            }}
                                        />
                                        <label htmlFor="agreement" className="text-sm leading-relaxed cursor-pointer">
                                            I have read and agree to the{' '}
                                            <span className="font-medium">Professional Services Agreement</span>{' '}
                                            and acknowledge that I have reviewed the{' '}
                                            <span className="font-medium">W-9 IRS Form</span>. I understand that this agreement will govern our business relationship with{' '}
                                            <span className="font-medium">LarkPM</span>.
                                        </label>
                                    </div>
                                    {errors.hasReadProfessionalServicesAgreement && (
                                        <p className="text-sm text-red-600 mt-2 ml-6 font-medium">{errors.hasReadProfessionalServicesAgreement.message}</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Digital Signature */}
                        <div>
                            <h3 className="font-newsreader text-xl font-semibold tracking-tight tracking-tight text-foreground mb-4">
                                Digital Signature<span className="text-red-600">*</span>
                            </h3>

                            {/* Signature Mode Toggle */}
                            <div className="flex gap-2 mb-4">
                                <Button
                                    type="button"
                                    variant={signatureMode === 'text' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => {
                                        setSignatureMode('text');
                                        setValue('signatureType', 'text');
                                        setValue('digitalSignature', '');
                                        trigger('digitalSignature');
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <Type className="h-4 w-4" />
                                    Type Signature
                                </Button>
                                <Button
                                    type="button"
                                    variant={signatureMode === 'drawn' ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => {
                                        setSignatureMode('drawn');
                                        setValue('signatureType', 'drawn');
                                        setValue('digitalSignature', '');
                                        signaturePadRef.current?.clear();
                                        trigger('digitalSignature');
                                    }}
                                    className="flex items-center gap-2"
                                >
                                    <Edit3 className="h-4 w-4" />
                                    Draw Signature
                                </Button>
                            </div>

                            <Card className={`border ${errors.digitalSignature ? 'border-red-300 bg-red-50' : 'border-border'}`}>
                                <CardContent className="p-6">
                                    {signatureMode === 'text' ? (
                                        <div className={`border-2 border-dashed rounded-lg p-8 text-center ${errors.digitalSignature ? 'border-red-300 bg-red-25' : 'border-border bg-muted'}`}>
                                            <p className="text-muted-foreground mb-4">Signature Area</p>
                                            <Input
                                                {...register('digitalSignature', {
                                                    onChange: () => trigger('digitalSignature')
                                                })}
                                                type="text"
                                                placeholder="Type your full name as your digital signature"
                                                className={`w-full max-w-md mx-auto p-3 border rounded-lg text-center font-script text-lg [font-family:cursive] ${errors.digitalSignature
                                                    ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                                                    : 'border-border focus:border-blue-500 focus:ring-blue-500'
                                                    }`}
                                            />
                                        </div>
                                    ) : (
                                        <div className="flex justify-center">
                                            <SignaturePad
                                                ref={signaturePadRef}
                                                value={watch('digitalSignature')}
                                                onChange={(signature) => {
                                                    setValue('digitalSignature', signature);
                                                    trigger('digitalSignature');
                                                }}
                                                width={500}
                                                height={200}
                                                className="mx-auto"
                                            />
                                        </div>
                                    )}
                                    {errors.digitalSignature && (
                                        <p className="text-sm text-red-600 mt-2 font-medium">{errors.digitalSignature?.message}</p>
                                    )}
                                </CardContent>
                            </Card>
                        </div>

                        {/* Legal Notice */}
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <p className="text-sm text-yellow-800">
                                <span className="font-medium">Legal Notice:</span> By providing your digital signature, you are legally agreeing to the terms and conditions outlined in the Professional Services Agreement. This signature has the same legal effect as a handwritten signature.
                            </p>
                        </div>
                    </>
                )}
            </div>
        </OnboardingWrapper>
    );
}
